import {
  FormControl,
  FormControlLabel,
  FormLabel,
  ListSubheader,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { ModalContainer, ModalStep, useModalSteps } from 'src/components/ModalContainer';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCreateWorkflow } from 'src/graphql/entities/workflow/hooks/use-create-workflow';
import {
  useWorkflowsByTrigger,
  type WorkflowByTriggerOption,
} from 'src/graphql/entities/workflow/hooks/use-workflows-by-trigger';
import {
  ORDERED_WORKFLOW_TRIGGER_TYPES,
  WORKFLOW_TRIGGER_LABELS,
} from 'src/graphql/entities/workflow/workflow.trigger';
import {
  getNewWorkflowDefaultValues,
  NewWorkflowField,
  type NewWorkflowFormValues,
  type NewWorkflowModalProps,
  START_FROM_BLANK,
} from 'src/pages/Settings/workflows/components/new-workflow-form';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';

const NAME_REQUIRED_MESSAGE = 'Name is required';

const STEP_TITLE = 'New workflow';

/**
 * Same arguments as `usePrefetchWorkflowAssignments`, which the page mounts: the lists are
 * then already in the cache when the dialog opens, rather than loading under keys of their
 * own. Inactive workflows are dropped below instead of by the query for the same reason.
 */
const START_FROM_QUERY_OPTIONS = { includeWithoutActiveVersion: true } as const;

/** Workflows this partner can start version 1 from, for one trigger: own, then platform. */
const useStartFromOptions = (partnerId: number | null) => {
  const { workflowsByTrigger: own } = useWorkflowsByTrigger(
    partnerId,
    START_FROM_QUERY_OPTIONS,
  );
  // Partner 0's own list already is the platform list; reading it twice would list it twice.
  const { workflowsByTrigger: platform } = useWorkflowsByTrigger(
    partnerId === PLATFORM_PARTNER_ID ? null : PLATFORM_PARTNER_ID,
    START_FROM_QUERY_OPTIONS,
  );
  const triggerType = useWatch<NewWorkflowFormValues, NewWorkflowField.TriggerType>({
    name: NewWorkflowField.TriggerType,
  });

  return useMemo(() => {
    // Nothing to copy from a workflow without an active version.
    const copyable = (options: WorkflowByTriggerOption[] = []) =>
      options.filter((option) => option.hasActiveVersion);

    return {
      own: copyable(own[triggerType]),
      platform: partnerId === PLATFORM_PARTNER_ID ? [] : copyable(platform[triggerType]),
    };
  }, [own, partnerId, platform, triggerType]);
};

const DetailsFields = () => {
  const { control } = useFormContext<NewWorkflowFormValues>();
  const { field: triggerField } = useController({
    control,
    name: NewWorkflowField.TriggerType,
  });

  return (
    <Stack spacing={2.5}>
      <FormTextField
        type="textfield"
        name={NewWorkflowField.Name}
        label="Name"
        fullWidth
        required={NAME_REQUIRED_MESSAGE}
        // `required` alone accepts a name of spaces; the backend trims and refuses it.
        rules={{ validate: (value: string) => !!value.trim() || NAME_REQUIRED_MESSAGE }}
        showErrorText
      />
      <FormControl>
        <FormLabel id="new-workflow-trigger-label">Trigger</FormLabel>
        <RadioGroup
          row
          aria-labelledby="new-workflow-trigger-label"
          name={triggerField.name}
          value={triggerField.value}
          onChange={(event) => triggerField.onChange(event.target.value)}
        >
          {ORDERED_WORKFLOW_TRIGGER_TYPES.map((triggerType) => (
            <FormControlLabel
              key={triggerType}
              value={triggerType}
              control={<Radio size="small" />}
              label={WORKFLOW_TRIGGER_LABELS[triggerType]}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <FormTextField
        type="textfield"
        name={NewWorkflowField.Description}
        label="Description (optional)"
        fullWidth
        textFieldProps={{ multiline: true, minRows: 2, maxRows: 4 }}
      />
    </Stack>
  );
};

const StartFromOptionGroup = ({
  heading,
  options,
}: {
  heading?: string;
  options: WorkflowByTriggerOption[];
}) =>
  options.length === 0 ? null : (
    <>
      {heading ? (
        <ListSubheader
          disableSticky
          disableGutters
          sx={{ lineHeight: 2.5, bgcolor: 'transparent' }}
        >
          {heading}
        </ListSubheader>
      ) : null}
      {options.map((option) => (
        <FormControlLabel
          key={option.workflowId}
          value={option.workflowId}
          control={<Radio size="small" />}
          label={`Copy of ${option.name}`}
        />
      ))}
    </>
  );

interface StartFromStepProps extends Pick<NewWorkflowModalProps, 'onCreated'> {
  onClose: () => void;
}

const StartFromStep = ({ onClose, onCreated }: StartFromStepProps) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showSuccessAlert } = useNotifications();
  const { control, handleSubmit } = useFormContext<NewWorkflowFormValues>();
  const { createWorkflowWithFirstVersion, isCreating } = useCreateWorkflow({
    partnerId: selectedPartnerId,
  });
  const { own, platform } = useStartFromOptions(selectedPartnerId);
  const { field: startFromField } = useController({
    control,
    name: NewWorkflowField.StartFrom,
  });

  // A pick made for the other trigger (changed after going back) no longer applies: read it
  // as Blank rather than resetting it, so switching back restores the pick.
  const startFrom = [...own, ...platform].some(
    (option) => option.workflowId === startFromField.value,
  )
    ? startFromField.value
    : START_FROM_BLANK;

  const submit = handleSubmit(async (values) => {
    const name = values[NewWorkflowField.Name].trim();
    const result = await createWorkflowWithFirstVersion({
      name,
      description: values[NewWorkflowField.Description].trim() || null,
      triggerType: values[NewWorkflowField.TriggerType],
      sourceWorkflowId: startFrom === START_FROM_BLANK ? null : startFrom,
      versionDescription: values[NewWorkflowField.VersionDescription].trim() || null,
    });

    if (!result) {
      return;
    }

    showSuccessAlert(`Workflow "${name}" created with version 1 as a draft`);
    // Not awaited: the page navigates in it, which must not hold the dialog open.
    void onCreated?.(result);
    onClose();
  });

  return (
    <ModalStep
      title={STEP_TITLE}
      actions={[
        { label: 'Back', kind: 'secondary' },
        {
          label: 'Create workflow',
          kind: 'primary',
          onClick: () => void submit(),
          loading: isCreating,
          loadingLabel: 'Creating…',
        },
      ]}
    >
      <Stack spacing={2.5}>
        <FormControl>
          <FormLabel id="new-workflow-start-from-label">Version 1 starts from</FormLabel>
          <RadioGroup
            aria-labelledby="new-workflow-start-from-label"
            name={startFromField.name}
            value={startFrom}
            onChange={(event) => startFromField.onChange(event.target.value)}
          >
            <FormControlLabel
              value={START_FROM_BLANK}
              control={<Radio size="small" />}
              label="Blank: a trigger and one Say step"
            />
            <StartFromOptionGroup
              heading={platform.length > 0 ? 'Your workflows' : undefined}
              options={own}
            />
            <StartFromOptionGroup heading="Platform workflows" options={platform} />
          </RadioGroup>
        </FormControl>
        <FormTextField
          type="textfield"
          name={NewWorkflowField.VersionDescription}
          label="Version note (optional)"
          fullWidth
        />
      </Stack>
    </ModalStep>
  );
};

const DetailsStep = ({ onClose, onCreated }: StartFromStepProps) => {
  const { pushStep } = useModalSteps();
  const { trigger } = useFormContext<NewWorkflowFormValues>();

  const goNext = useCallback(async () => {
    if (await trigger(NewWorkflowField.Name)) {
      void pushStep(<StartFromStep onClose={onClose} onCreated={onCreated} />);
    }
  }, [onClose, onCreated, pushStep, trigger]);

  return (
    <ModalStep
      title={STEP_TITLE}
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        { label: 'Next', kind: 'primary', onClick: () => void goNext() },
      ]}
    >
      <DetailsFields />
    </ModalStep>
  );
};

/**
 * New workflow: step 1 names it and picks its trigger, step 2 picks what version 1 starts
 * from. One form spans both steps so going Back keeps what was typed.
 */
export const NewWorkflowModal = () => {
  const { closeModal } = useModalMethods(Modals.NewWorkflow);
  const { isOpened, modalProps } = useModalState(Modals.NewWorkflow);

  // Read once, at mount: ModalProvider mounts only the open modal, so each opening is a fresh
  // form with its own prefill (Copy to new workflow, or a clean one).
  const formMethods = useForm<NewWorkflowFormValues>({
    defaultValues: getNewWorkflowDefaultValues({
      initialTriggerType: modalProps?.initialTriggerType,
      initialSourceWorkflowId: modalProps?.initialSourceWorkflowId,
    }),
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...formMethods}>
      <ModalContainer open={isOpened} size="medium" onClose={closeModal}>
        <DetailsStep onClose={closeModal} onCreated={modalProps?.onCreated} />
      </ModalContainer>
    </FormProvider>
  );
};
